import { UserSettingsContext } from "@hoylu/user-settings";
import { FrameContexts } from "@microsoft/teams-js";
import React, { useCallback, useContext, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "ts-debounce";
import {
  config as selectConfig,
  enableFavoritesWorkspaceSection as selectEnableFavoritesWorkspaceSection,
} from "../state/config/config.selector";
import { AppMode } from "../state/mode/mode.reducer";
import type { AppState } from "../state/root.reducer";
import {
  selectUserIsLoggedIn,
  selectUserOrgBannerUrl,
} from "../state/user/user.selector";
import {
  selectDashboardSelectedProject,
  isAdminOfSelectedProject as selectIsAdminOfSelectedProject,
} from "../state/workspaces/projects.selector";
import * as workspacesActions from "../state/workspaces/workspaces.actions";
import {
  selectDashboardIncludeHiddenWorkspaces,
  selectFullscreenWorkspaceId,
  selectIsCreditLimitReached,
  selectMyWorkspacesMetadataLists,
  selectWorkspacesPagination,
  selectWorkspacesSearchLabelsString,
  selectWorkspacesSearchTerm,
} from "../state/workspaces/workspaces.selector";
import { allLocalizations, Localized } from "../strings";
import { useI18n } from "../utils/hooks/use.i18n";
import { managementPortalBillingUrl } from "../utils/management.portal/management.portal.url.selectors";
import { openManagementPortal } from "../utils/management.portal/openManagementPortal";
import { dropInvalidExternalLink } from "../utils/url.helpers";
import { CreateWorkspaceSection } from "./CreateWorkspaceSection";
import WorkspaceCard from "./cards/WorkspaceCard/WorkspaceCard";
import ErrorFetchingWorkspace from "./ErrorFetchingWorkspace";
import Header from "./Header/Header";
import { LoadMoreWorkspacesButton } from "./LoadMoreWorkspacesButton";
import SectionLayout from "./layouts/SectionLayout";
import { WorkspacesLayout } from "./layouts/WorkspacesLayout";
import { LiveWorkspacesContainer } from "./live.workspaces.container";
import OrgBanner from "./OrgBanner";
import ProjectSection from "./ProjectSection";
import TeamsSidePanel from "./TeamsSidePanel";
import Styles from "./WorkspaceContent.module.css";

export const WorkspaceContent = () => {
  const dispatch = useDispatch();
  const settingsContext = useContext(UserSettingsContext);
  const t = useI18n();

  const debouncedDispatch = useCallback(debounce(dispatch, 750), [dispatch]);

  const appMode = useSelector((state: AppState) => state.mode);
  const bannerLink = useSelector((state: AppState) =>
    dropInvalidExternalLink(state.context.user.orgBannerLink),
  );
  const billingUrl = useSelector(managementPortalBillingUrl);
  const config = useSelector(selectConfig);
  const isAdminOfSelectedProject = !!useSelector(
    selectIsAdminOfSelectedProject,
  );
  const isLoggedIn = useSelector(selectUserIsLoggedIn);
  const isOverCreditLimit = useSelector(selectIsCreditLimitReached);
  const isWorkspaceFullscreen = !!useSelector(selectFullscreenWorkspaceId);
  const orgBannerUrl = useSelector(selectUserOrgBannerUrl);
  const showFavoritesSection = useSelector(
    selectEnableFavoritesWorkspaceSection,
  );
  const selectedProject = useSelector(selectDashboardSelectedProject);

  // Pagination and searching/filtering
  const searchTerm = useSelector(selectWorkspacesSearchTerm);
  const searchLabels = useSelector(selectWorkspacesSearchLabelsString);
  const includeHidden = useSelector(selectDashboardIncludeHiddenWorkspaces);
  const { data, fetchFailure } = useSelector(selectWorkspacesPagination);

  const workspacesLists = useSelector((state: AppState) =>
    selectMyWorkspacesMetadataLists(
      state,
      settingsContext.userSettings?.favoriteWorkspaces,
    ),
  );

  const showOrgBanner = isLoggedIn && !!orgBannerUrl;

  useEffect(() => {
    dispatch(workspacesActions.getProjects());
    dispatch(workspacesActions.getDashboardLabels.request());
  }, []);

  useEffect(() => {
    if (![AppMode.DASHBOARD, AppMode.LOADING_DASHBOARD].includes(appMode))
      return;

    debouncedDispatch(
      selectedProject
        ? workspacesActions.getWorkspacesOfProject.request({
            projectId: selectedProject.id,
            labels: searchLabels,
            name: searchTerm,
          })
        : workspacesActions.loadWorkspaces.request({
            includeHidden,
            labels: searchLabels,
            name: searchTerm,
            offset: 0,
          }),
    );

    return () => {
      debouncedDispatch.cancel();
    };
  }, [
    appMode,
    debouncedDispatch,
    includeHidden,
    searchLabels,
    searchTerm,
    selectedProject,
  ]);

  useEffect(() => {
    const currentLanguage = settingsContext.userSettings?.currentLanguage;

    // "en" is set as the default on a workspace init
    if (currentLanguage && currentLanguage !== "en") {
      // TODO: [i18n] useI18n hook internally uses Localized in returned callback, but Localized is not reactive. Pushing language will not trigger returning a new callback by useI18n.
      Localized.pushLocale(currentLanguage, allLocalizations[currentLanguage]);
    }
  }, [settingsContext.userSettings?.currentLanguage]);

  const showCreateWorkspaceSection =
    !selectedProject ||
    (isAdminOfSelectedProject && selectedProject.status !== "Inactive");

  const onEditWorkspaceClicked = (workspaceId: string) => {
    dispatch(workspacesActions.editWorkspace({ workspaceId }));
  };

  if (config.teamsFrame === FrameContexts.sidePanel) {
    return <TeamsSidePanel />;
  }

  return (
    <>
      {!isWorkspaceFullscreen && <Header />}
      <div className={Styles.workspaceContent}>
        {showOrgBanner && (
          <OrgBanner bannerLink={bannerLink} orgBanner={orgBannerUrl} />
        )}
        {isOverCreditLimit && !selectedProject && (
          <div className={Styles.creditOverLimit}>
            <h1>{t("CREDIT_LIMIT_WARNING_BANNER.TITLE")}</h1>
            <span>&nbsp;</span>
            <h1
              className={Styles.billingLink}
              onClick={() => openManagementPortal(billingUrl)}
            >
              {t("CREDIT_LIMIT_WARNING_BANNER.BUTTON")} <span>&gt;</span>
            </h1>
          </div>
        )}
        {selectedProject && <ProjectSection />}
        {showCreateWorkspaceSection && (
          <div className={Styles.restrictedView}>
            <SectionLayout
              ariaLabel={t("SECTIONS.START_FROM_TEMPLATE.LABEL")}
              className={Styles.createWorkspaceSectionLayout}
            >
              <CreateWorkspaceSection />
            </SectionLayout>
          </div>
        )}
        {fetchFailure && <ErrorFetchingWorkspace />}
        {showFavoritesSection && workspacesLists.favorites.length > 0 && (
          <SectionLayout title={t("SECTIONS.FAVORITES")}>
            <WorkspacesLayout>
              {workspacesLists.favorites.map((w) => (
                <WorkspaceCard
                  editWorkspaceAction={onEditWorkspaceClicked}
                  isFavorite
                  key={w.workspaceId}
                  myWorkspaceMetadata={w}
                />
              ))}
            </WorkspacesLayout>
          </SectionLayout>
        )}
        {workspacesLists.hidden.length > 0 && (
          <SectionLayout title={t("SECTIONS.HIDDEN")}>
            <WorkspacesLayout>
              {workspacesLists.hidden.map((w) => (
                <WorkspaceCard
                  editWorkspaceAction={onEditWorkspaceClicked}
                  key={w.workspaceId}
                  myWorkspaceMetadata={w}
                />
              ))}
            </WorkspacesLayout>
          </SectionLayout>
        )}
        <SectionLayout title={t("SECTIONS.WORKSPACES_LIST")}>
          <WorkspacesLayout
            loadNextPage={() => {
              dispatch(
                workspacesActions.loadWorkspaces.request({
                  includeHidden,
                  labels: searchLabels,
                  name: searchTerm,
                  offset: data?.offset || undefined,
                }),
              );
            }}
          >
            {workspacesLists.base.map((w) => (
              <WorkspaceCard
                editWorkspaceAction={onEditWorkspaceClicked}
                key={w.workspaceId}
                myWorkspaceMetadata={w}
              />
            ))}
          </WorkspacesLayout>
        </SectionLayout>
        <LoadMoreWorkspacesButton />
      </div>
      <LiveWorkspacesContainer />
    </>
  );
};

export default WorkspaceContent;
